import type {
  DraftMessageId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  DraftMessageId as DraftMessageIdSchema,
  SupportTicketId as SupportTicketIdSchema,
} from "@redotech/effect-api-shared/ids/definitions";
import type { SaveDraftInput } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import type { SupportTicketDraftResponse } from "@redotech/effect-api-shared/rpc/support-ticket";
import { BasicIndex } from "@tanstack/db";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { Option } from "effect";
import { requireSupportTicketIdScope } from "../query/support-ticket-collection-scope";
import { supportTicketDraftQueryKeys } from "../query/support-ticket-query-keys";
import { parseCollectionFilters } from "../query/support-ticket-tanstack-scope";
import {
  buildSaveDraftPayload,
  type DraftCollectionItem,
} from "./support-ticket-draft-requests";

export interface SupportTicketDraftsApi {
  getDrafts(
    supportTicketId: SupportTicketId,
  ): Promise<{ drafts: readonly SupportTicketDraftResponse[] }>;
  saveDraft(
    supportTicketId: SupportTicketId,
    payload: SaveDraftInput,
  ): Promise<void>;
  deleteDraft(
    supportTicketId: SupportTicketId,
    draftId: DraftMessageId,
  ): Promise<void>;
}

export interface CreateSupportTicketDraftsCollectionOptions {
  queryClient: QueryClient;
  api: SupportTicketDraftsApi;
}

export function createDraftsCollection({
  queryClient,
  api,
}: CreateSupportTicketDraftsCollectionOptions) {
  return createCollection<DraftCollectionItem, string>(
    queryCollectionOptions({
      queryKey: supportTicketDraftQueryKeys.all,
      queryFn: async (ctx): Promise<DraftCollectionItem[]> => {
        const supportTicketId = requireSupportTicketIdScope(
          parseCollectionFilters(ctx.meta?.loadSubsetOptions),
        );
        const result = await api.getDrafts(supportTicketId);
        return result.drafts.map((d) => ({
          ...d,
          inReplyToMessageId: Option.getOrUndefined(
            d.inReplyToMessageId,
          )?.toString(),
        }));
      },
      queryClient,
      getKey: (item) => item.id.toString(),
      syncMode: "on-demand",
      autoIndex: "eager",
      defaultIndexType: BasicIndex,
      onInsert: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const item = mutation.modified;
          await api.saveDraft(
            SupportTicketIdSchema.make(item.supportTicketId),
            buildSaveDraftPayload(item),
          );
        }
        await transaction.mutations[0].collection.utils.refetch();
      },
      onUpdate: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const item = mutation.modified;
          await api.saveDraft(
            SupportTicketIdSchema.make(item.supportTicketId),
            buildSaveDraftPayload(item),
          );
        }
        return { refetch: false };
      },
      onDelete: async ({ transaction }) => {
        for (const mutation of transaction.mutations) {
          const item = mutation.original as DraftCollectionItem;
          await api.deleteDraft(
            SupportTicketIdSchema.make(item.supportTicketId),
            DraftMessageIdSchema.make(String(mutation.key)),
          );
        }
      },
    }),
  );
}

export type DraftsCollection = ReturnType<typeof createDraftsCollection>;
